import {
    MutableRefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { useFocusEffect } from '@react-navigation/native';
import {
    addDays,
    endOfDay,
    format,
    getDay,
    getMonth,
    isAfter,
    isBefore,
    isSameDay,
    millisecondsToSeconds,
    secondsToMilliseconds,
    startOfDay,
    subDays,
} from 'date-fns';

import { EPGAlignment, IEPG } from 'custom_modules/react-native-epg';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import {
    DATE_PICKER_DYNAMIC_ID,
    DATE_PICKER_TODAY_ID,
    DATE_PICKER_TOMORROW_ID,
    DATE_PICKER_YESTERDAY_ID,
} from 'config/testID';

import { ModularScreens, ModularScreensArray } from 'router/types';

import { setEpgDate } from 'store/epg/actions';
import { EpgRequestedBy } from 'store/epg/reducer';
import { selectEpgParams } from 'store/epg/selectors';

import useHeaderHeight from 'utils/hooks/useHeaderHeight';
import usePrevious from 'utils/hooks/usePrevious';
import { navigationRef } from 'utils/navigation/navigationUtils';
import {
    isPhone,
    isTablet,
    isWeb,
    widthPercentageToDP,
} from 'utils/responsiveness';

import {
    PICKER_HEIGHT,
    PICKER_MARGIN,
} from 'screens/Epg/components/EpgGridDatePicker';
import { PRIME_TIME } from 'screens/Epg/utils/config';
import getTimeLineDays from 'screens/Epg/utils/get-timeline-days';

export enum MainDateItemKeys {
    TODAY = 'today',
    YESTERDAY = 'yesterday',
    TONIGHT = 'tonight',
    TOMORROW = 'tomorrow',
}

export type DateItem = {
    date: Date;
    label: string;
    isSelected: boolean;
    key: MainDateItemKeys | string;
    testID?: string;
};

export type Position = {
    top: number;
    left: number;
};

const useDatePicker = (
    epgRef: MutableRefObject<IEPG | null>,
    epgY: number | undefined,
    screenReaderEnabled: boolean,
) => {
    const bottomSheetModalRef = useRef<BottomSheetModal>(undefined);
    const now: Date = new Date();
    const startDate = addDays(now, -7);
    const endDate = addDays(now, 15);

    const { start: epgDataStartTime } = useSelector(selectEpgParams);

    const selectedDate = secondsToMilliseconds(epgDataStartTime);
    const prevSelectedDate = usePrevious(selectedDate, undefined);

    const dispatch = useDispatch();

    const [selectedDateLabel, setSelectedDateLabel] = useState<string>('');
    const [previousDateLabel, setPreviousDateLabel] = useState<string>('');
    const [nextDateLabel, setNextDateLabel] = useState<string>('');
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const { t } = useTranslation();
    const headerHeight = useHeaderHeight();

    const modalStartingPosition = useMemo<Position | undefined>(
        () =>
            epgY !== undefined
                ? {
                      left: isTablet ? 44 : widthPercentageToDP(72),
                      top: epgY + headerHeight - PICKER_HEIGHT - PICKER_MARGIN,
                  }
                : undefined,
        [epgY, headerHeight],
    );

    const dataItems = getTimeLineDays(startDate, endDate);

    const getDateLabel = useCallback(
        (date: number): string => {
            if (
                isBefore(date, subDays(now, 2)) ||
                isAfter(date, addDays(now, 1))
            ) {
                return `${t(
                    LANGUAGE_KEYS.EPG.DAY_TO_TRANSLATION[getDay(date)],
                )}, ${format(date, 'dd')} ${t(
                    LANGUAGE_KEYS.EPG.MONTH_TO_TRANSLATION[getMonth(date)],
                )}`;
            }

            if (isSameDay(date, subDays(now, 1))) {
                return t('DAY_NAME_YESTERDAY');
            }

            if (isSameDay(now, date)) {
                return t('DAY_NAME_TODAY');
            }

            if (isSameDay(addDays(now, 1), date)) {
                return t('DAY_NAME_TOMORROW');
            }

            return '';
        },
        [t, now],
    );

    const getSideItems = useCallback(
        (before: boolean): DateItem[] => {
            const items: DateItem[] = [];

            dataItems.forEach((item) => {
                if (
                    before
                        ? isBefore(item, subDays(now, 2))
                        : isAfter(item, addDays(now, 1))
                ) {
                    items.push({
                        date: item,
                        label: `${t(
                            LANGUAGE_KEYS.EPG.DAY_TO_TRANSLATION[getDay(item)],
                        )}, ${format(item, 'dd')} ${t(
                            LANGUAGE_KEYS.EPG.MONTH_TO_TRANSLATION[
                                getMonth(item)
                            ],
                        )}`,
                        isSelected: isSameDay(item, selectedDate),
                        key: `${t(
                            LANGUAGE_KEYS.EPG.DAY_TO_TRANSLATION[getDay(item)],
                        )}`,
                        testID: `${DATE_PICKER_DYNAMIC_ID} ${format(
                            item,
                            'dd',
                        )}${t(
                            LANGUAGE_KEYS.EPG.MONTH_TO_TRANSLATION[
                                getMonth(item)
                            ],
                        )}`,
                    });
                }
            });

            return items;
        },
        [now, dataItems, selectedDate, t],
    );

    const data = useMemo(
        (): DateItem[] => [
            ...getSideItems(true),
            {
                date: subDays(now, 1),
                label: t('DAY_NAME_YESTERDAY'),
                isSelected: isSameDay(subDays(now, 1), selectedDate),
                key: MainDateItemKeys.YESTERDAY,
                testID: DATE_PICKER_YESTERDAY_ID,
            },
            {
                date: now,
                label: t('DAY_NAME_TODAY'),
                isSelected: isSameDay(now, selectedDate),
                key: MainDateItemKeys.TODAY,
                testID: DATE_PICKER_TODAY_ID,
            },
            {
                date: addDays(now, 1),
                label: t('DAY_NAME_TOMORROW'),
                isSelected: isSameDay(addDays(now, 1), selectedDate),
                key: MainDateItemKeys.TOMORROW,
                testID: DATE_PICKER_TOMORROW_ID,
            },
            ...getSideItems(false),
        ],
        [selectedDate, t, now],
    );

    const getSelectedDateLabel = useCallback(() => {
        return getDateLabel(selectedDate);
    }, [selectedDate, getDateLabel]);

    const getPreviousDateLabel = useCallback(() => {
        const newDay = addDays(selectedDate, -1);

        if (isBefore(newDay, subDays(new Date(), 8))) {
            return '';
        }

        return getDateLabel(newDay.getTime());
    }, [selectedDate, getDateLabel]);

    const getNextDateLabel = useCallback(() => {
        const newDay = addDays(selectedDate, 1);

        if (isAfter(newDay, addDays(new Date(), 14))) {
            return '';
        }

        return getDateLabel(newDay.getTime());
    }, [selectedDate, getDateLabel]);

    useEffect(() => {
        setSelectedDateLabel(getSelectedDateLabel());
    }, [getSelectedDateLabel]);

    useEffect(() => {
        setTimeout(
            () => {
                setPreviousDateLabel(getPreviousDateLabel());
            },
            prevSelectedDate ? 300 : 0,
        );
    }, [getPreviousDateLabel, prevSelectedDate]);

    useEffect(() => {
        setTimeout(
            () => {
                setNextDateLabel(getNextDateLabel());
            },
            nextDateLabel ? 300 : 0,
        );
    }, [getNextDateLabel, nextDateLabel]);

    const handleOpen = useCallback(() => {
        if (isTablet || isWeb || (isPhone && screenReaderEnabled)) {
            setIsModalVisible(true);
        } else if (bottomSheetModalRef.current) {
            bottomSheetModalRef.current?.present();
            setIsModalVisible(true);
        }
    }, [screenReaderEnabled]);

    const handleClose = useCallback(() => {
        if (isTablet || isWeb || (isPhone && screenReaderEnabled)) {
            setIsModalVisible(false);
        } else if (bottomSheetModalRef.current) {
            bottomSheetModalRef.current?.dismiss();
            setIsModalVisible(false);
        }
    }, [screenReaderEnabled]);

    const onDateSelect = useCallback(
        (date, isNow) => {
            if (date) {
                if (!isSameDay(selectedDate, date)) {
                    dispatch(
                        setEpgDate({
                            start: millisecondsToSeconds(
                                startOfDay(date).getTime(),
                            ),
                            end: millisecondsToSeconds(
                                endOfDay(date).getTime(),
                            ),
                            requestedBy: EpgRequestedBy.DATE_PICKER,
                        }),
                    );
                }

                if (
                    isNow &&
                    prevSelectedDate &&
                    isSameDay(prevSelectedDate, date)
                ) {
                    epgRef.current?.scrollToNow();
                }
            }
        },
        [selectedDate, isModalVisible, dispatch, prevSelectedDate],
    );

    const onPressDatePickerItem = useCallback(
        (item: DateItem) => {
            handleClose();
            if (item.key === MainDateItemKeys.TODAY) {
                onDateSelect(new Date(), true);
            } else {
                onDateSelect(
                    !isSameDay(item.date, selectedDate) ? item.date : null,
                    false,
                );
            }
        },
        [onDateSelect, selectedDate],
    );

    const onPressPrevDateMarker = useCallback(() => {
        const prevDay = subDays(selectedDate, 1);

        dispatch(
            setEpgDate({
                start: millisecondsToSeconds(startOfDay(prevDay).getTime()),
                end: millisecondsToSeconds(endOfDay(prevDay).getTime()),
                requestedBy: EpgRequestedBy.PREV_DAY_JUMP_MARKER,
            }),
        );
    }, [selectedDate, dispatch]);

    const onPressNextDateMarker = useCallback(() => {
        const nextDay = addDays(selectedDate, 1);

        dispatch(
            setEpgDate({
                start: millisecondsToSeconds(startOfDay(nextDay).getTime()),
                end: millisecondsToSeconds(endOfDay(nextDay).getTime()),
                requestedBy: EpgRequestedBy.NEXT_DAY_JUMP_MARKER,
            }),
        );
    }, [selectedDate, dispatch]);

    const onPressPrimeTimeJumpMarker = useCallback(() => {
        epgRef.current?.scrollToTime(
            new Date(selectedDate).setHours(PRIME_TIME, 0, 0, 0),
            EPGAlignment.center,
            true,
        );
    }, [selectedDate]);

    const onPressNowJumpMarker = useCallback(() => {
        const currentDate = new Date();

        if (isSameDay(currentDate, selectedDate)) {
            epgRef.current?.scrollToNow();
        } else {
            dispatch(
                setEpgDate({
                    start: millisecondsToSeconds(
                        startOfDay(currentDate).getTime(),
                    ),
                    end: millisecondsToSeconds(endOfDay(currentDate).getTime()),
                    requestedBy: EpgRequestedBy.NOW_JUMP_MARKER,
                }),
            );
        }
    }, [selectedDate, dispatch]);

    useFocusEffect(
        useCallback(() => {
            return () => {
                const routeName =
                    navigationRef.current?.getCurrentRoute()?.name;

                if (
                    routeName &&
                    ModularScreensArray.includes(routeName as ModularScreens)
                ) {
                    onDateSelect(new Date(), true);
                }
            };
        }, [onDateSelect]),
    );

    return {
        startDate,
        selectedDateLabel,
        bottomSheetModalRef,
        onDateSelect,
        handleOpen,
        dateData: data,
        isModalVisible,
        handleClose,
        modalStartingPosition,
        onPressDatePickerItem,
        previousDateLabel,
        nextDateLabel,
        onPressPrimeTimeJumpMarker,
        onPressPrevDateMarker,
        onPressNextDateMarker,
        onPressNowJumpMarker,
    };
};

export default useDatePicker;
